import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Home, Users, MessageSquare,
  Menu, X, LogOut, User, ChevronDown, Search, Bell,
  UserCog, FolderKanban, Image, Building2, Palette, Share2, Globe, TrendingUp, Plug, Megaphone, Mail, GitBranch, FileText
} from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://content-review-api.ryantoth2410.workers.dev/api';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number;
}

interface NavigationSection {
  title: string;
  items: NavigationItem[];
}

export default function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [notifications, setNotifications] = useState(0);
  const [searchFocused, setSearchFocused] = useState(false);

  const navigationSections: NavigationSection[] = [
    {
      title: '',
      items: [
        { name: 'Overview', href: '/dashboard', icon: Home },
      ]
    },
    {
      title: 'Organizations & Clients',
      items: [
        { name: 'Organizations', href: '/dashboard/organizations', icon: Building2 },
        { name: 'Contacts', href: '/dashboard/contacts', icon: Users },
        { name: 'Brands', href: '/dashboard/brands', icon: Palette },
      ]
    },
    {
      title: 'Projects & Assets',
      items: [
        { name: 'Projects', href: '/dashboard/projects', icon: FolderKanban },
        { name: 'Workflow', href: '/dashboard/workflow', icon: GitBranch },
        { name: 'Assets', href: '/dashboard/assets', icon: Image },
      ]
    },
    {
      title: 'Content',
      items: [
        { name: 'Content Hub', href: '/dashboard/content', icon: FileText },
        { name: 'Social', href: '/dashboard/social', icon: Share2 },
        { name: 'Websites', href: '/dashboard/websites', icon: Globe },
        { name: 'SEO', href: '/dashboard/seo', icon: TrendingUp },
        { name: 'Ads', href: '/dashboard/ads', icon: Megaphone },
        { name: 'Emails', href: '/dashboard/emails', icon: Mail },
        { name: 'Design', href: '/dashboard/design', icon: Palette },
      ]
    },
    {
      title: 'Settings',
      items: [
        { name: 'Integrations', href: '/dashboard/integrations', icon: Plug },
      ]
    },
  ];

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token) {
      navigate('/login');
      return;
    }

    // Set auth header
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    if (userData) {
      setUser(JSON.parse(userData));
    }

    // Verify token is still valid
    checkAuth();
  }, [navigate]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/check`);
      if (!response.data.authenticated) {
        handleLogout();
      }
    } catch (error) {
      // Silent fail - auth check endpoint might not exist
      console.log('Auth check skipped');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    navigate('/login');
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Left Sidebar */}
      <div className={`${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col`}>

        {/* Sidebar Header */}
        <div className="flex items-center justify-between h-16 px-6 bg-gradient-to-r from-blue-600 to-blue-700 flex-shrink-0">
          <h1 className="text-xl font-bold text-white">Content Review</h1>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-white hover:bg-blue-500 rounded p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Sidebar Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {navigationSections.map((section, sectionIdx) => (
            <div key={sectionIdx} className={sectionIdx > 0 ? 'mt-6' : ''}>
              {section.title && (
                <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {section.title}
                </h3>
              )}
              <div className="space-y-1">
                {section.items.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                        isActive
                          ? 'bg-blue-50 text-blue-700 shadow-sm'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                      }`
                    }
                  >
                    <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                    <span className="flex-1">{item.name}</span>
                    {item.badge && item.badge > 0 && (
                      <span className="ml-auto bg-blue-600 text-white text-xs rounded-full px-2 py-0.5 min-w-[20px] text-center">
                        {item.badge}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Sidebar Footer */}
        <div className="flex-shrink-0 border-t border-gray-200 p-4">
          <button className="flex items-center w-full text-sm text-gray-600 hover:text-gray-900 transition">
            <MessageSquare className="h-5 w-5 mr-2" />
            <span>Feedback</span>
            {notifications > 0 && (
              <span className="ml-auto bg-blue-600 text-white text-xs rounded-full px-2 py-0.5">
                {notifications}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top header */}
        <header className="bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <div className="flex items-center flex-1">
              {/* Mobile menu button */}
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 rounded-md text-gray-400 lg:hidden hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              >
                <Menu className="h-6 w-6" />
              </button>

              {/* Search bar */}
              <div className="ml-4 lg:ml-0 flex-1 max-w-2xl">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search projects, clients, content..."
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setSearchFocused(false)}
                    className={`pl-10 pr-4 py-2 w-full border rounded-lg text-sm transition-all ${
                      searchFocused
                        ? 'border-blue-500 ring-2 ring-blue-100'
                        : 'border-gray-300'
                    } focus:outline-none`}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3 ml-4">
              {/* Notifications */}
              <button className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition">
                <Bell className="h-5 w-5" />
                {notifications > 0 && (
                  <span className="absolute top-1.5 right-1.5 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-white" />
                )}
              </button>

              {/* User menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center text-sm rounded-lg p-1.5 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white">
                    <User className="h-5 w-5" />
                  </div>
                  <span className="ml-2 text-gray-700 font-medium hidden sm:block">
                    {user?.name || user?.email || 'Admin'}
                  </span>
                  <ChevronDown className={`ml-1 h-4 w-4 text-gray-400 hidden sm:block transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                    <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
                      <div className="p-2">
                        <div className="px-3 py-2 border-b border-gray-100">
                          <p className="text-sm font-medium text-gray-900">{user?.name || 'Admin User'}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{user?.email || 'admin@contentreview.com'}</p>
                        </div>
                        <button
                          onClick={() => {
                            setUserMenuOpen(false);
                            navigate('/dashboard/team');
                          }}
                          className="flex items-center w-full px-3 py-2 mt-1 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition"
                        >
                          <UserCog className="mr-3 h-4 w-4" />
                          Manage Team
                        </button>
                        <button
                          onClick={handleLogout}
                          className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition"
                        >
                          <LogOut className="mr-3 h-4 w-4" />
                          Sign out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}